import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IHarvestTanks, OpService } from "../../../../../util/services/operation";
import { ACTION_TYPE, TIME_PER_STEP } from '../../../../../util/varibles/constants';
import {
    IActivityLog,
    IDeliveryTypeUnit,
    IOperation,
    IUnit,
    TOperation
} from "../../../../../util/varibles/interface";
import { getActivityLogDurationEst, getFactorySiteTime } from "../../../util";
import { resolveActivityLog } from "../../../util/function_operation/global";
import { getEditOp } from "../EditOperationContext";

interface IProps {
    data: ({ isCall?: boolean } & IOperation)
    startTime?: number
    startPlaceId?: string
    startPlaceField?: string

    onLoading(loading: boolean, isCall?: boolean): void

    onChange(op?: Partial<TOperation>, activity_logs?: IActivityLog[]): void;
}

const UpdateActivityLog: React.FC<IProps> = ({
    data,
    startTime,
    startPlaceId,
    startPlaceField,
    onLoading,
    onChange
}) => {
    const [parameterPartOne, setParameterPartOne] = useState<string>(JSON.stringify({}));
    const [parameterPartTwo, setParameterPartTwo] = useState<string>(JSON.stringify({}));
    const timeout = useRef<ReturnType<typeof setTimeout>>();
    const abort = useRef<AbortController>();
    const onLoadingRef = useRef(onLoading);
    const onChangeRef = useRef(onChange);
    const getEdit = getEditOp();
    const getEditRef = useRef(getEdit);
    getEditRef.current = getEdit;
    const dataRef = useRef(data);
    dataRef.current = data;
    const { id: vessel_id, type: vesselType } = data.vessel;
    const { isCall } = data;
    const {
        factory_id: rootFactoryId,
        site_id: rootSiteId,
        is_journey_to_site,
        is_cleaning,
        sub_operations,
        error_detail,
        tanks,
        router_id,
    } = data.operation;
    const getSubs = useRef<() => IUnit[]>(() => sub_operations);
    const isTanks = useRef<boolean>(tanks.length > 0);
    const isRouteError = !!error_detail?.route;

    const config = useCallback((time: number) => {
        const {
            factory_time,
            factoryTime,
            site_arrival_time,
            priority
        } = getFactorySiteTime(rootSiteId, rootFactoryId, vesselType, time);
        return { site_id: rootSiteId, factory_id: rootFactoryId, factory_time, factoryTime, site_arrival_time, priority, router_id };

    }, [rootSiteId, rootFactoryId, router_id, vesselType]);

    useEffect(() => {
        onLoadingRef.current = onLoading;
        onChangeRef.current = onChange;
    }, [onLoading, onChange]);

    useEffect(() => {
        setParameterPartOne(prev => {
            if (!startTime)
                return JSON.stringify({})
            else {
                if (isRouteError)
                    return prev;

                const { site_id, factory_id, factory_time, factoryTime, site_arrival_time, priority, router_id } = config(startTime);
                const value = {
                    start_time: startTime,
                    factory_time,
                    packing_time: factoryTime,
                    site_arrival_time,
                    priority,
                    start_place_id: startPlaceId,
                    start_place_field: startPlaceField,
                    source_id: site_id,
                    destination_id: factory_id,
                    vessel_id,
                    is_journey_to_site,
                    is_cleaning,
                    router_id,
                    isCall: false
                }
                if (prev === JSON.stringify(value)) {
                    return prev;
                } else {
                    value.isCall = true;
                    return JSON.stringify(value)
                }
            }
        })
    }, [is_journey_to_site, is_cleaning, config, vessel_id, startTime, startPlaceId, startPlaceField, isRouteError]);

    useEffect(() => {
        getSubs.current = () => sub_operations;
        setParameterPartTwo(prev => {
            const valuePartTwo = JSON.parse(prev) || {};
            const value = sub_operations.reduce((rs: IHarvestTanks['sub_operations'], item) => {
                const { id = '', unit_id = '', avg_weight, fish_amount, delivery_types = [] } = item;
                let newTotalWeight = 0;
                const newDeliveryTypes = delivery_types.reduce((subRs: IDeliveryTypeUnit[], sub) => {
                    const { id, name, fish_amount, total_weight, tanks } = sub;
                    if (!total_weight)
                        return subRs;
                    const newSubTotalWeight = avg_weight > 0 ? fish_amount * avg_weight : total_weight;
                    newTotalWeight += newSubTotalWeight;
                    return [...subRs, {
                        id,
                        name,
                        fish_amount,
                        total_weight: newSubTotalWeight,
                        tanks
                    }];
                }, []);
                return newDeliveryTypes.length > 0 ? [...rs, {
                    id,
                    unit_id,
                    avg_weight,
                    fish_amount,
                    total_weight: newTotalWeight,
                    delivery_types: newDeliveryTypes
                }] : rs
            }, []);
            const next = JSON.stringify({
                isCall: value.length > 0 && isCall && (JSON.stringify(value) !== JSON.stringify(valuePartTwo.sub_operations || [])),
                sub_operations: value
            })
            return prev === next ? prev : next;
        })
    }, [sub_operations, isCall]);

    useEffect(() => {
        const { isCall: isCallPartOne, ...valuePartOne } = JSON.parse(parameterPartOne) || {};
        const { isCall: isCallPartTwo, ...valuePartTwo } = JSON.parse(parameterPartTwo) || {};
        if (!isCallPartOne && !isCallPartTwo) {
            return;
        }

        if (timeout.current) {
            clearTimeout(timeout.current);
            abort.current?.abort();
        }

        if (isTanks.current) {
            isTanks.current = false;
            return;
        }

        onLoadingRef.current(true);
        timeout.current = setTimeout(() => {
            abort.current = new AbortController();
            const { start_time, ...argsPartOne } = valuePartOne;
            const body = {
                start_time: Math.ceil((Math.max(start_time, Date.now())) / TIME_PER_STEP) * TIME_PER_STEP,
                ...argsPartOne,
                ...valuePartTwo
            }
            const current = dataRef.current;
            const controller = abort.current;
            const baseline = getEditRef.current().editMode === ACTION_TYPE.CREATE
                ? Promise.resolve(current.operation.duration_est)
                : getActivityLogDurationEst(current, controller);
            baseline
                .then(async durationEst => {
                    const rs = await OpService.harvestTanks(body, controller);
                    const { activity_log, sub_operations: newSubs, ...operation } = rs;
                    const resolved = resolveActivityLog(dataRef.current.activity_log, activity_log, durationEst);
                    const oldSubs = getSubs.current();
                    const objectNewSubs = newSubs.reduce((rs: {
                        [id: string]: { [id: string]: IDeliveryTypeUnit }
                    }, item) => {
                        const { delivery_types = [], id, } = item;
                        rs[id] = {
                            ...rs[id] || {}, ...delivery_types.reduce((subRs: {
                                [id: string]: IDeliveryTypeUnit
                            }, sub) => {
                                subRs[sub.id] = sub;
                                return subRs
                            }, {})
                        }

                        return rs;
                    }, {})
                    setParameterPartOne(JSON.stringify({ ...valuePartOne, router_id: operation.router_id, isCall: false }));
                    isTanks.current = false;
                    onChangeRef.current({
                        ...operation,
                        duration_est: resolved.duration_est,
                        sub_operations: oldSubs.map((item) => {
                            return {
                                ...item,
                                delivery_types: (item.delivery_types || []).map((sub) => ({ ...sub, ...objectNewSubs[item.id]?.[sub.id] || {} }))
                            }
                        })
                    }, resolved.activity_log)
                })
                .catch(() => {
                    onLoadingRef.current(false, true)
                })
        }, 1200)
    }, [parameterPartOne, parameterPartTwo]);

    return null;
};

export default UpdateActivityLog;
